import { RefreshCw, RotateCcw, Save } from 'lucide-react'

import { Button, ErrorNotice, SelectField, StatusMessage } from '@krp/ui-kit'

import { OneCQueryState } from '../../../shared/ui/OneCQueryState'
import type { CreateDefaultsActions, CreateDefaultsFieldVm, CreateDefaultsVm } from '../model/types'
import '../../../shared/ui/onec.css'
import '../../pricing/ui/onec-ui.css'

/** Пустой выбор: у папки — корень справочника, у обязательного поля — «решить при применении». */
function emptyLabel(field: CreateDefaultsFieldVm): string {
  return field.required ? '— не задано: выбирать при каждом создании —' : '— без папки (в корень справочника) —'
}

function FieldRow({ field, onChange, disabled }: { field: CreateDefaultsFieldVm; onChange: (value: string) => void; disabled: boolean }) {
  return (
    <div className="onec-row">
      <SelectField
        label={field.required ? `${field.label} *` : field.label}
        value={field.value}
        options={[{ value: '', label: emptyLabel(field) }, ...field.options]}
        onChange={onChange}
        disabled={disabled}
      />
      {field.stale && (
        <p className="onec-field__note" role="note">
          Сохранённое значение больше не найдено в справочнике базы (удалено или помечено на удаление) — выберите заново.
        </p>
      )}
      {!field.stale && field.missing && (
        <p className="onec-field__note" role="note">
          Не задано: товар из синхронизации без выбора этого поля при применении в эту базу не создастся.
        </p>
      )}
    </div>
  )
}

/**
 * Окно «Создание по умолчанию» базы 1С (RP-SYN11, SYNC-V2-PLAN 3.7): чем
 * заполнить поля новой карточки, которых нет у других источников (вид
 * номенклатуры, папка, единица, ставка НДС), когда синхронизация создаёт
 * товар в этой базе. Значения — только из справочников базы (служба читает их
 * из 1С на лету). В окне применения синхронизации они подставлены заранее и
 * правятся для всех строк сразу или для каждой.
 */
export function CreateDefaultsScreen({ vm, actions }: { vm: CreateDefaultsVm; actions: CreateDefaultsActions }) {
  if (vm.query.status !== 'ready') {
    return (
      <section className="onec-section" aria-label="Создание по умолчанию">
        <OneCQueryState query={vm.query} emptyText="У этой базы нет полей создания для выбора" onRetry={actions.retry} />
      </section>
    )
  }
  return (
    <section className="onec-section" aria-labelledby="onec-create-defaults">
      <div className="onec-section__head">
        <h3 id="onec-create-defaults">Создание по умолчанию</h3>
        <Button variant="ghost" icon={<RefreshCw size={16} aria-hidden="true" />} onClick={actions.retry} disabled={vm.save.busy}>
          Перечитать из 1С
        </Button>
      </div>
      <p className="onec-field__note">
        Когда синхронизация заводит товар в этой базе, поля ниже берутся отсюда, если при применении не выбрано другое. Поля со звёздочкой
        обязательны: без значения товар не создаётся, и применение назовёт поле.
      </p>
      {vm.fields.map((field) => (
        <FieldRow key={field.key} field={field} onChange={(value) => actions.changed(field.key, value)} disabled={vm.save.busy} />
      ))}
      {vm.lostFields.length > 0 && (
        <StatusMessage
          kind="empty"
          title="Сохранённое значение больше не действует"
          text={`В базе больше нет справочника для поля: ${vm.lostFields.join(', ')}. При сохранении значение будет снято.`}
        />
      )}
      {vm.save.error !== null && <ErrorNotice title="Настройки не сохранены" error={vm.save.error} />}
      <div className="onec-row">
        <Button variant="primary" icon={<Save size={16} aria-hidden="true" />} busy={vm.save.busy} disabled={!vm.dirty || vm.save.busy} onClick={actions.save}>
          Сохранить
        </Button>
        {vm.dirty && (
          <Button variant="ghost" icon={<RotateCcw size={16} aria-hidden="true" />} onClick={actions.reset} disabled={vm.save.busy}>
            Отменить изменения
          </Button>
        )}
      </div>
      <p className="onec-field__note">
        {vm.savedAt === null ? 'Настройки ещё не сохранялись.' : `Сохранено: ${vm.savedAt}${vm.savedBy === null ? '' : ` · ${vm.savedBy}`}`}
      </p>
    </section>
  )
}
